/**
 * Discipline — Alan (domain) tipleri.
 *
 * Bu dosya framework'ten bağımsızdır: içinde DOM, IndexedDB veya
 * herhangi bir kütüphane yoktur. Böylece hem veritabanı katmanı
 * (`db/database.ts`), hem puanlama motoru (Faz 2), hem de ekranlar
 * aynı tipleri paylaşabilir.
 *
 * Buradaki alan adları CLAUDE.md §7'deki iş kurallarının birebir
 * karşılığıdır — bir formülü değiştirmen gerekirse önce §7'yi, sonra
 * burayı güncelle.
 */

/* ==================================================================
   Kimlik ve ortak tipler
   ================================================================== */

export type HabitId = string;
export type LogId = string;
export type ChallengeId = string;
export type CleaningRoomId = string;
export type CleaningTaskId = string;
export type RewardId = string;
export type TodoItemId = string;

/**
 * Takvim günü, `yyyy-mm-dd` biçiminde (örn. '2026-09-05').
 * Saat/dakika bilgisi bilinçli olarak yok: tüm iş kuralları "gün"
 * bazında çalışıyor ve saat taşımak, saat dilimi kaynaklı gün kayması
 * hatalarına davetiye çıkarırdı.
 */
export type IsoDate = string;

/**
 * Uygulamadaki tüm çizgi ikonların adları.
 * Faz 3'te her ad için bir SVG çizimi yazılacak; tip birliği sayesinde
 * var olmayan bir ikon adı yazarsan TypeScript derlemede yakalar.
 * Alışkanlıklar ve temizlik odaları aynı havuzdan ikon seçer.
 */
export type IconName =
  | 'water'
  | 'moon'
  | 'walk'
  | 'smoke'
  | 'book'
  | 'apple'
  | 'broom'
  | 'dumbbell'
  | 'laptop'
  | 'social'
  | 'toothbrush'
  | 'cube'
  | 'home'
  | 'trophy'
  | 'checklist'
  | 'user'
  | 'flame'
  | 'gift'
  | 'pencil';

/* ==================================================================
   Alışkanlıklar (CLAUDE.md §7.1)
   ================================================================== */

/**
 * Alışkanlığın nasıl işaretlendiği:
 * - `boolean` : yapıldı / yapılmadı (alışkanlıkların çoğu).
 * - `partial` : Bölünme Oranı ile işaretlenir (Su iç, Yürüyüş gibi).
 * - `auto`    : kullanıcı işaretlemez, sistem hesaplar. Şu an yalnızca
 *               "Temizlik" bu türde (bkz. §7.5).
 */
export type HabitKind = 'boolean' | 'partial' | 'auto';

/**
 * Otomatik bir alışkanlığın durumunu HANGİ sistem kuralının
 * hesapladığı.
 *
 * `kind: 'auto'` tek başına "sistem hesaplar" demek; ama hangi
 * kuralın hesapladığını söylemez. Bu ayrım olmadan ikinci bir
 * otomatik alışkanlık eklendiğinde kod ilk bulduğunu alır ve temizlik
 * durumunu yanlış alışkanlığa yazardı.
 *
 * Yeni bir otomatik kaynak eklemek için: buraya bir değer ekle, onu
 * hesaplayan `sync...Log` fonksiyonunu yaz ve `AUTO_SOURCES`
 * listesine koy. Alışkanlık formu yeni kaynağı kendiliğinden sunar.
 */
export type AutoSource = 'cleaning';

/**
 * Şu an fiilen HESAPLANAN otomatik kaynaklar.
 *
 * Bir kaynak, arkasında çalışan bir kural olmadan buraya
 * eklenmemeli: hesaplayanı olmayan otomatik bir alışkanlık her gün
 * "yapılmadı" kalır ve §7.2'ye göre her gün ceza yazar.
 */
export const AUTO_SOURCES: AutoSource[] = ['cleaning'];

/**
 * Bölünme Oranı — bir alışkanlığın o gün ne kadarının yapıldığı.
 * §7.1 gereği %50'nin altında bir seçenek YOKTUR; 0 ise "o gün hiç
 * yapılmadı" demektir, ara bir değer değildir.
 * Boolean ve auto alışkanlıklarda yalnızca 0 veya 1 kullanılır.
 */
export type CompletionRatio = 0 | 0.5 | 0.75 | 1;

/**
 * Alışkanlığın (isteğe bağlı) kategorisi.
 * Şu anki iş kurallarının hiçbirinde kullanılmıyor; ileride gruplama
 * veya filtreleme istenirse hazır dursun diye korundu.
 */
export type HabitCategory =
  | 'nutrition'
  | 'sleep'
  | 'reading'
  | 'screenTime'
  | 'exercise'
  | 'custom';

export interface Habit {
  id: HabitId;
  title: string;
  /** Alışkanlık satırında solda görünen ikon. */
  icon: IconName;
  category: HabitCategory;
  kind: HabitKind;
  /**
   * Yalnızca `kind === 'auto'` olduğunda anlamlıdır: bu alışkanlığın
   * günlük durumunu hangi sistem kuralının hesapladığını söyler.
   * Her kaynak en fazla BİR alışkanlığa bağlanır.
   */
  autoSource?: AutoSource;
  /**
   * Başarı Puanı — bu alışkanlığın "ham" değeri (§7.2).
   * Katsayı ile çarpılarak günlük katkıyı verir. Varsayılan 1.
   */
  successScore: number;
  /**
   * Katsayı — Başarı Puanı'nı ölçekleyen ondalıklı çarpan (§7.2).
   * Örn. 1.5 = bu alışkanlık normalin 1.5 katı değerinde.
   */
  coefficient: number;
  /**
   * Anasayfa listesindeki sıra. IndexedDB kayıtları anahtar (UUID)
   * sırasında geldiği için, kullanıcının gördüğü sıra rastgele
   * olmasın diye açık bir sıralama alanı tutuyoruz.
   */
  sortOrder: number;
  /** Alışkanlığın oluşturulduğu gün. */
  createdAt: IsoDate;
  /**
   * Yumuşak silme (soft delete). Silinen alışkanlık listeden kalkar
   * ama kaydı durur — çünkü geçmiş `dailyScoreLedger` kayıtları o
   * alışkanlığın katkısıyla hesaplanmıştı ve §7.2 gereği geçmiş asla
   * yeniden hesaplanmaz. Bu yüzden asla hard-delete yapılmaz (Faz 9).
   */
  archived: boolean;
}

export interface HabitLog {
  id: LogId;
  habitId: HabitId;
  /** Bu kaydın ait olduğu gün. */
  date: IsoDate;
  /**
   * O gün alışkanlığın ne kadarının yapıldığı — tek doğru kaynak.
   * Ayrı bir `completed` bayrağı bilinçli olarak YOKTUR: iki alanın
   * birbirinden sapması (biri "yapıldı", diğeri 0) puanlama ile
   * ekranın çelişmesine yol açardı.
   *   • "Yapıldı mı?"        → completionRatio > 0
   *   • "Streak'e sayılır mı?" → completionRatio >= 0.5  (§7.3)
   */
  completionRatio: CompletionRatio;
  note?: string;
}

/* ==================================================================
   Günlük puan defteri (CLAUDE.md §7.2)
   ================================================================== */

/**
 * Bir günün kapanmış, DEĞİŞTİRİLEMEZ (immutable) puan kaydı.
 *
 * Gün sonunda (ya da kullanıcı ertesi gün uygulamayı ilk açtığında)
 * bir kez yazılır ve bir daha asla güncellenmez. Bir alışkanlığın
 * Puanı/Katsayısı sonradan değişse bile geçmiş günler etkilenmez —
 * §7.2'deki "geçmişe dönük bağışıklık" kuralı budur.
 *
 * `date` aynı zamanda birincil anahtardır: hem her gün için tek kayıt
 * olmasını garanti eder, hem de kayıtları tarih sırasında okumayı
 * (Faz 16 "Detaylı Geçmiş" ve ileride dışa aktarım) bedelsiz kılar.
 */
export interface DailyScoreLedger {
  date: IsoDate;
  /** Yuvarlama öncesi ham net toplam (ondalıklı olabilir). */
  grossDelta: number;
  /** `Math.trunc(grossDelta)` — sıfıra doğru yuvarlanmış tam sayı. */
  finalDelta: number;
  /** Bu değişim uygulandıktan sonraki Başarı Barı değeri (0–1000). */
  barValueAfter: number;
  /**
   * O gün bir tatil hakkı kullanılmış mıydı? (§7.7)
   * Ceza hesabının atlandığı günleri geçmişte de ayırt edebilmek için
   * kaydediliyor — aksi halde düşük ama cezasız bir gün, normal bir
   * günden ayırt edilemezdi.
   */
  wasVacationDay: boolean;
  /**
   * O gün ödülle mi satın alınmıştı? (§7.6)
   *
   * Tatil günüyle aynı etkiyi yapar (ceza yok, seri korunur) ama
   * KAYNAĞI farklıdır — geçmişte ikisini ayırt edebilmek için ayrı
   * tutuluyor. Bu alan eklenmeden önce kapanmış günlerde bulunmaz;
   * yokluğu "ödül günü değildi" demektir.
   */
  wasRewardDay?: boolean;
  /**
   * O gün Day Streak'e sayıldı mı? (§7.3)
   *
   * Sonradan yeniden hesaplanamadığı için kaydedilmek ZORUNDA:
   * uygunluk, o günkü alışkanlık listesine ve oranlarına bağlıdır.
   * Kullanıcı sonradan alışkanlık ekler/arşivlerse, aynı günün
   * kayıtlarından bugün yapılacak bir hesap farklı sonuç verirdi.
   * §7.2'deki "geçmişe dönük bağışıklık" ilkesi burada da geçerli:
   * karar gün kapanırken bir kez verilir ve dondurulur.
   */
  wasStreakEligible: boolean;
}

/* ==================================================================
   Genel ilerleme durumu
   ================================================================== */

/** Tek kayıtlı (singleton) store'lar için sabit anahtar. */
export const SINGLETON_ID = 'singleton';
export type SingletonId = typeof SINGLETON_ID;

/**
 * Uygulamanın "şu anki" genel durumu — tek satırlık bir kayıt.
 *
 * Not: Aşağıdaki `Streak` tipi alışkanlık BAŞINA seri tutuyordu;
 * CLAUDE.md §7.3'teki Day Streak ise tüm uygulama için TEK bir
 * sayaçtır. Bu yüzden Day Streak burada, global olarak tutulur.
 */
export interface ProgressState {
  id: SingletonId;
  /**
   * Başarı Barı'nın **son kapanmış günün sonundaki** değeri (0–1000).
   *
   * Dikkat: bugünün henüz kesinleşmemiş değişimini İÇERMEZ. Ekranda
   * gösterilen anlık sayı `barValue + bugünün canlı değişimi` olarak
   * hesaplanır (§7.2). Böylece gün içinde yapılan işaretlemeler kalıcı
   * değere yazılmaz; gün kapanışı tek ve kesin bir yazma işlemidir.
   *
   * İstisna: challenge iptali (−50) ve ödül kullanımı (−100) bu değere
   * doğrudan ve anında uygulanır — §7.4 ve §7.6 gereği bunlar günlük
   * alışkanlık hesabından bağımsızdır.
   */
  barValue: number;
  /** Kesintisiz Day Streak (§7.3). */
  currentStreak: number;
  /** Şimdiye kadarki en uzun seri — sadece gösterim amaçlı. */
  longestStreak: number;
  /**
   * `dailyScoreLedger`'a kaydı kapatılmış son gün.
   * Uygulama her açıldığında bugünün tarihiyle karşılaştırılır; arada
   * kapatılmamış günler varsa onlar sırayla kapatılır (§7.2'deki gün
   * değişimi kuralı). Hiç gün kapanmadıysa `null`.
   */
  lastFinalizedDate: IsoDate | null;
  /**
   * Ödülle satın alınan günler (§7.6).
   *
   * Ödül artık sayaca körlemesine +1 eklemez; BELİRLİ BİR GÜNÜ satın
   * alır ve o günü tatil günü gibi dondurur: ceza işlemez, gün seriye
   * sayılır. Hangi günün satın alındığını bilmeden bunu yapmak mümkün
   * değil — sayaç tek başına "hangi gün?" sorusunu cevaplayamaz.
   *
   * Aynı gün hem tatil hem ödül kullanılamaz (§7.6); bu liste o
   * kontrolün de kaynağıdır.
   */
  rewardDates: IsoDate[];
}

/* ==================================================================
   Challenge'lar (CLAUDE.md §7.4)
   ================================================================== */

/**
 * - `active`    : şu an devam eden challenge (aynı anda en fazla 1 tane).
 * - `completed` : süresi dolmuş ve başarıyla bitmiş.
 * - `cancelled` : kullanıcı süresi dolmadan iptal etti (−50 puan).
 */
export type ChallengeStatus = 'active' | 'completed' | 'cancelled';

/**
 * Kullanıcının süreyi girerken seçtiği birim. Sistem her zaman gün
 * cinsinden saklar (§7.4); bu alan yalnızca ekranda "1 ay" yazabilmek
 * için, yani girdinin nasıl yazıldığını hatırlamak için tutulur.
 * "1 ay" her zaman sabit 30 gündür.
 */
export type ChallengeDurationUnit = 'day' | 'month';

/** "1 ay" seçildiğinde kullanılacak sabit gün sayısı (§7.4). */
export const DAYS_PER_CHALLENGE_MONTH = 30;

/** Bir challenge iptal edilince Başarı Barı'ndan düşen sabit ceza (§7.4). */
export const CHALLENGE_CANCEL_PENALTY = 50;

export interface Challenge {
  id: ChallengeId;
  title: string;
  /** Örn. "40 gün boyunca 10.000 adım". Boş bırakılabilir. */
  description: string;
  startDate: IsoDate;
  /** startDate + durationDays - 1 (bitiş günü dahil). */
  endDate: IsoDate;
  /** Süre her zaman gün cinsinden saklanır. */
  durationDays: number;
  /** Kullanıcının süreyi girerken seçtiği birim (yalnızca gösterim). */
  durationInputUnit: ChallengeDurationUnit;
  status: ChallengeStatus;
  /**
   * Challenge'ın başarıyla tamamlandığı günler.
   * Hem ilerleme halkasındaki "18 / 30 gün" sayısını, hem §7.3'teki
   * "challenge günü 1 alışkanlık yapılmış gibi sayılır" kuralını bu
   * liste besler. Puanlamaya (§7.2) GİRMEZ.
   */
  completedDates: IsoDate[];
  /** İptal edildiyse iptal günü. */
  cancelledAt?: IsoDate;
  linkedHabitId?: HabitId;
}

/* ==================================================================
   Temizlik (CLAUDE.md §7.5)
   ================================================================== */

/**
 * Ev bölümü. Sabit bir oda listesi YOKTUR — odaları tamamen kullanıcı
 * tanımlar (Profil > Temizlik Görevlerini Düzenle, Faz 10).
 */
export interface CleaningRoom {
  id: CleaningRoomId;
  name: string;
  /** Oda kartının başındaki ikon. */
  icon: IconName;
  /** Temizlik sayfasındaki 2 sütunlu gridde görünme sırası. */
  sortOrder: number;
}

export interface CleaningTask {
  id: CleaningTaskId;
  roomId: CleaningRoomId;
  name: string;
  /**
   * Döngü süresi (gün). Görev son tamamlanmasının üzerinden bu kadar
   * gün geçince "sırası geldi" sayılır ve bildirim tetiklenir (§7.5).
   */
  cycleDays: number;
  /**
   * Görevin en son tamamlandığı gün. Hiç yapılmadıysa `null` — bu
   * durumda görev sırası gelmiş kabul edilir (ilk kez yapılmayı
   * bekliyor).
   */
  lastCompletedDate: IsoDate | null;
  /**
   * Bir öncekinin tarihi — YALNIZCA bugün yanlışlıkla yapılan bir
   * işaretlemeyi geri alabilmek için tutulur.
   *
   * Gerekçe: görev tamamlanınca `lastCompletedDate` bugüne çekilir ve
   * görev `cycleDays` kadar ileri atılır. Bu bilgi olmadan yanlış bir
   * dokunuş geri alınamaz; kullanıcı görevin haftalar sonraya
   * kaymasını seyretmek zorunda kalırdı. Tam bir tamamlanma geçmişi
   * tutmuyoruz çünkü geri alınabilecek tek şey bugünkü işaretleme.
   */
  previousCompletedDate: IsoDate | null;
  /** Odanın içindeki görev sırası. */
  sortOrder: number;
}

/* ==================================================================
   Tatil hakkı (CLAUDE.md §7.7)
   ================================================================== */

/** Kaç günlük kesintisiz seride bir tatil hakkı yenilenir. */
export const VACATION_STREAK_MILESTONE = 90;

/** Day Streak kaynaklı hakkın tamamlandığı tavan (§7.7). */
export const VACATION_STREAK_CAP = 10;

/**
 * Hakkın nereden geldiği.
 * - `dayStreak` : 90 günlük kesintisiz seri kilometre taşı. §7.7'deki
 *                 10 günlük TAVAN yalnızca bu kaynağa uygulanır.
 * - `manual`    : ileride eklenecek diğer kaynaklar (özel durum, ödül
 *                 vb.) için ayrılmış. Bunlar havuzu 10'un üstüne
 *                 çıkarabilir ve tavan onları asla kırpmaz.
 */
export type VacationGrantSource = 'dayStreak' | 'manual';

/**
 * Havuza yapılan tek bir ekleme kaydı.
 * Ekleme geçmişini tutuyoruz ki tavanın hangi kaynağa uygulandığı
 * (ve ileride hangi kaynağın ne kadar verdiği) sonradan da bilinsin.
 */
export interface VacationGrant {
  id: string;
  /** Bu eklemeyle havuza giren gün sayısı. */
  days: number;
  source: VacationGrantSource;
  grantedAt: IsoDate;
  /** `dayStreak` kaynaklı ise hangi kilometre taşı (90, 180, 270...). */
  streakMilestone?: number;
}

export interface VacationAllowance {
  id: SingletonId;
  /** Şimdiye kadar havuza giren toplam gün (tüm kaynaklar dahil). */
  totalDaysGranted: number;
  /** Şimdiye kadar harcanan gün sayısı = `usedDates.length`. */
  daysUsed: number;
  /** Hakkın ilk kez açıldığı gün; hiç açılmadıysa `null`. */
  unlockedAt: IsoDate | null;
  /** Havuza yapılan tüm eklemelerin kaydı. */
  grants: VacationGrant[];
  /**
   * Tatil hakkının kullanıldığı günler.
   * Puanlama motorunun "bu gün tatil günü müydü?" sorusunu
   * cevaplayabilmesi için gerekli — §7.7'de tatil günü cezasızdır
   * ama kazanç normal işler.
   */
  usedDates: IsoDate[];
}

/* ==================================================================
   Ödüller (CLAUDE.md §7.6)
   ================================================================== */

/**
 * Ödül tanımı. Şu an tek bir statik ödül var, bu yüzden veritabanında
 * saklanmıyor — kod içinde sabit olarak duruyor (aşağıdaki `REWARDS`).
 * Ödül sayısı artar ve kullanıcı tanımlı hale gelirse kendi store'una
 * taşınır.
 */
export interface Reward {
  id: RewardId;
  title: string;
  description: string;
  /** Başarı Barı'ndan doğrudan düşülecek puan (§7.6). */
  costInScore: number;
  /** Karşılığında Day Streak'e eklenecek gün sayısı. */
  grantsStreakDays: number;
}

/** Şu anki tek ödül: 100 Başarı Puanı → 1 Day Streak. */
export const REWARDS: readonly Reward[] = [
  {
    id: 'streak-day',
    title: '100 Başarı Puanı → 1 Gün Streak',
    description: 'Biriken puanlarını harcayarak serine bir gün ekle.',
    costInScore: 100,
    grantsStreakDays: 1,
  },
];

/* ==================================================================
   Başarı Barı sınırları ve varsayılanlar (CLAUDE.md §7.2)
   ================================================================== */

/** Başarı Barı'nın alt sınırı — bunun altına asla inmez. */
export const SCORE_BAR_MIN = 0;
/** Başarı Barı'nın üst sınırı (tavan) — bunun üstüne asla çıkmaz. */
export const SCORE_BAR_MAX = 1000;
/** Uygulama ilk açıldığında Başarı Barı'nın başlangıç değeri (§7.2). */
export const INITIAL_BAR_VALUE = 100;

/** Bir günün streak'e sayılması için gereken en düşük oran (§7.3). */
export const STREAK_ELIGIBILITY_THRESHOLD = 0.5;

/** Yeni alışkanlık oluşturulurken kullanılan varsayılan değerler. */
export const DEFAULT_SUCCESS_SCORE = 1;
export const DEFAULT_COEFFICIENT = 1;

/* ==================================================================
   Yapılacaklar (Faz 14)
   ================================================================== */

/**
 * Yapılacak maddesi.
 *
 * ÖNEMLİ — Yapılacaklar, Başarı Barı'na ve Day Streak'e KATILMAZ.
 * Alışkanlık değildir; §7.2'deki formüle girmez ve §7.3'teki %50
 * hesabının paydasını değiştirmez. Aksi halde bir yapılacak maddesi
 * eklemek, o günün ceza tabanını sessizce yükseltirdi — kullanıcı
 * "listeye bir şey ekledim, puanım düştü" durumuyla karşılaşırdı.
 *
 * Bildirim ayarı HER MADDE İÇİN AYRIDIR (§7.8.4): madde kendi
 * saatlerini taşır.
 */
export interface TodoItem {
  id: TodoItemId;
  /** Kullanıcının yazdığı metin — "İlaç al" gibi. */
  title: string;
  /** Tamamlandıysa tamamlanma günü, değilse `null`. */
  completedDate: IsoDate | null;
  /** Oluşturulma günü — listede eskiden yeniye sıralamak için. */
  createdAt: IsoDate;
  /** Açık maddeler listesindeki elle belirlenen sıra. */
  sortOrder: number;
  /** Bu madde için hatırlatma gönderilsin mi? (§7.8.4) */
  notificationsEnabled: boolean;
  /**
   * Hatırlatma saatleri, YEREL saatle `HH:MM` biçiminde.
   * Bildirim sayısı bu dizinin uzunluğudur; ayrı bir sayaç tutmuyoruz
   * ki ikisi birbirinden ayrı düşemesin.
   * Her gün tekrar eder (§7.8.4) ve en fazla `TODO_MAX_NOTIFICATIONS`
   * adet olabilir.
   */
  notificationTimes: string[];
}

/**
 * Bir maddeye kurulabilecek en fazla bildirim sayısı (§7.8.4).
 * Ücretsiz plan bütçesi ölçüldü; bu sayı sorun çıkarmıyor (§7.8.5).
 */
export const TODO_MAX_NOTIFICATIONS = 10;

/** Bir madde tamamlanmış mı? Tek doğru kaynak `completedDate`. */
export function isTodoCompleted(item: TodoItem): boolean {
  return item.completedDate !== null;
}

/** Madde başlığı için üst sınır — bildirime sığması gerekiyor (§7.8.4). */
export const TODO_TITLE_MAX_LENGTH = 80;

/* ==================================================================
   Adım verisi (backend health-sync köprüsü)
   ================================================================== */

export interface StepSample {
  date: IsoDate;
  steps: number;
  /** Örneğin backend tarafından alındığı an (ISO zaman damgası). */
  syncedAt: string;
}

/* ==================================================================
   Kullanımdan kalkanlar
   ================================================================== */

/**
 * @deprecated Alışkanlık başına seri. CLAUDE.md §7.3'teki Day Streak
 * tüm uygulama için TEK bir sayaç olduğundan bu tip artık hiçbir iş
 * kuralında kullanılmıyor — yerine `ProgressState.currentStreak`
 * geçti. İskeletten kalan `streaks` store'uyla birlikte, veri kaybı
 * riski olmadığından emin olunca (Faz 17) silinecek.
 */
export interface Streak {
  habitId: HabitId;
  currentLength: number;
  longestLength: number;
  lastCompletedDate: IsoDate | null;
}
